import { ModalComponent } from "@core/components/alt-ui/modal";
import { Vue, Component, Prop } from "vue-property-decorator";
import { BFormGroup, BFormInput, BFormTextarea, BInputGroupAppend, BButton } from "bootstrap-vue";
import { BListGroup, BListGroupItem, BCardText, BPagination } from "bootstrap-vue";
import { Currency, IAccount, IEmployee, IPayment, ISale, ISalePaymentCreateDto, Locale } from "@lib";
import { SalePaymentModal } from "./sale-payment.modal";
import { Formatter } from "@/utils/formatter";

@Component({
    components: {
        BFormGroup,
        BFormTextarea,
        BFormInput,
        BButton,
        BInputGroupAppend,
        BListGroup,
        BListGroupItem,
        BCardText,
        BPagination,
        ModalComponent,
    },
})
export default class SaleViewTabsPayments extends Vue {
    @Prop({ required: true })
    private sale!: ISale;

    @Prop({ type: Array, default: () => [] })
    private employees!: IEmployee[];

    @Prop({ type: Array, default: () => [] })
    private accounts!: IAccount[];

    @Prop({ type: Boolean, default: false })
    private readonly!: boolean;

    private SalePaymentUseCase = this.$alt.system.usecase.createSalePaymentUseCase();
    private salePaymentModal!: SalePaymentModal;

    constructor() {
        super();

        this.salePaymentModal = new SalePaymentModal("sale-view-tabs-payments-modal");
        this.salePaymentModal.onCreate = this.createPayment.bind(this);
    }

    private get payments(): IPayment[] {
        if (!this.sale || !this.sale.payments) {
            return [];
        }

        return this.sale.payments;
    }

    private get total(): number {
        if (!this.sale || !this.sale.payments) {
            return 0.0;
        }

        let sum = 0.0;
        for (const payment of this.sale.payments) {
            sum += payment.value;
        }
        return sum;
    }

    private get currency(): Currency | undefined {
        return this.sale?.shopRef?.info?.currency;
    }

    private get locale(): Locale | undefined {
        return this.sale?.shopRef?.info?.locale;
    }

    private getEmployeeName(id: string): string {
        if (!this.employees) {
            return id;
        }
        const employee = this.employees.find((emp: any) => emp.user === id);
        return employee ? employee.userRef.info.name : id;
    }

    private getAccountName(id: string): string {
        if (!this.accounts) {
            return id;
        }
        const account = this.accounts.find(a => a.id === id);
        return account ? account.info.name : id;
    }

    private onSaleChanged(sale: ISale): void {
        this.$emit("sale-changed", sale, this.sale);
    }

    //

    public async showCreateModal(): Promise<void> {
        const user = await this.$info.getUser();
        await this.salePaymentModal.show({
            accounts: this.accounts,
            userId: user.id,
        });
    }

    public async confirmPaymentDelete(payment: IPayment): Promise<void> {
        const money = Formatter.money(payment.value, { currency: this.currency, locale: this.locale });

        const confirm = await this.$alt.message.confirm(
            `Вы уверены, что хотите удалить платёж: "${payment.description}" (${money})?`,
            "Удаление платежа",
            { acceptText: "Удалить" },
        );

        if (confirm) {
            await this.deletePayment(payment);
        }
    }

    //

    private async createPayment(dto: ISalePaymentCreateDto): Promise<ISale | null> {
        try {
            this.$alt.loader.show();

            const companyId = this.sale.company;
            const shopId = this.sale.shop;
            const sale = await this.SalePaymentUseCase.create(companyId, shopId, this.sale.id, dto);
            this.onSaleChanged(sale);
            return sale;
        } catch {
            this.$alt.toast.error("Не удалось добавить платёж.");
            return null;
        } finally {
            this.$alt.loader.hide();
        }
    }

    private async deletePayment(payment: IPayment): Promise<void> {
        try {
            this.$alt.loader.show();

            const companyId = this.sale.company;
            const shopId = this.sale.shop;
            const sale = await this.SalePaymentUseCase.delete(companyId, shopId, this.sale.id, payment.id);
            this.onSaleChanged(sale);
        } catch {
            this.$alt.toast.error("Не удалось удалить платёж.");
        } finally {
            this.$alt.loader.hide();
        }
    }
}
